import React, { useState, useId } from 'react';
import classnames from 'classnames';
import dateFormat from 'dateformat';
import Row from 'reactstrap/lib/Row';
import Col from 'reactstrap/lib/Col';
import PropTypes from 'prop-types';
import { maxBy, minBy, get, head } from 'lodash-es';
import { formatPriceStringV2 } from 'site-modules/shared/utils/price-utils';
import {
  getYAxisRoundingIncrementForMinAndMax,
  formatPriceInThousands,
  roundToNearestIncrement,
  ROUND_UP,
  ROUND_DOWN,
} from 'site-modules/shared/utils/inventory/price-drop-graph-utils';
import { Line, XAxis, YAxis, ResponsiveContainer, Area, ComposedChart, ReferenceLine } from 'recharts';
import { TIME } from 'client/utils/time';
import { getDealRating, NO_RATING } from 'site-modules/shared/utils/car-buying/deal-rating-util';
import { GraphActiveDot } from 'site-modules/shared/components/inventory/price-history-graph/graph-active-dot';
import { PriceDropGraphTooltip } from 'site-modules/shared/components/inventory/price-drop-graph/price-drop-graph-tooltip';

import './price-drop-graph.scss';

const DEBOUNCE = 800;
const GRAPH_HEIGHT = 200;
const AREA_STROKE_COLOR = '#58B883';

export const PriceDropGraph = ({
  currentPrice,
  history = [],
  margin = {
    top: 5,
    right: 30,
    left: 10,
    bottom: 5,
  },
  firstPublishedDate = 0,
  wrapperClasses = '',
  showPriceChanges = false,
  averagePrice,
  dealType = null,
}) => {
  const [tooltipData, setTooltipData] = useState(null);
  const originalPrice = get(head(history), 'amount');
  const noHistory = history && history.length === 1 && originalPrice === currentPrice;
  const [currentDate] = useState(() => Date.now());
  const randomTooltipInt = useId();

  if ((!history.length || noHistory) && !showPriceChanges) {
    return null;
  }

  const numberOfFullDays = Math.floor((currentDate - firstPublishedDate) / TIME.MS.ONE_DAY);
  const dateMask = numberOfFullDays < 365 ? 'm/d' : 'm/d/yy';
  const data = [];
  let prevPrice;
  let isLastPriceIncrease;

  history.forEach(({ amount, timestamp }) => {
    data.push({
      name: dateFormat(timestamp, dateMask),
      timestamp,
      amount,
      isPriceIncrease: prevPrice < amount,
      isOriginalPrice: !prevPrice,
      randomTooltipInt,
    });
    isLastPriceIncrease = prevPrice < amount;
    prevPrice = amount;
  });

  const isCurrentPriceIncrease = isLastPriceIncrease && prevPrice === currentPrice;

  data.push({
    name: dateFormat(currentDate, dateMask),
    timestamp: currentDate,
    amount: currentPrice,
    isPriceIncrease: isCurrentPriceIncrease || prevPrice < currentPrice,
    isCurrentPrice: true,
    randomTooltipInt,
  });

  const maxVal = maxBy(data, 'amount').amount;
  const minVal = minBy(data, 'amount').amount;

  const yAxisRoundingIncrementForMinAndMax = getYAxisRoundingIncrementForMinAndMax({
    minVal: Math.min(minVal, averagePrice || minVal), // the `||` case handles averagePrice is zero or NaN
    maxVal: Math.max(maxVal, averagePrice || maxVal),
  });
  const yMax = roundToNearestIncrement({
    value: Math.max(maxVal, averagePrice || maxVal),
    nearestIncrement: yAxisRoundingIncrementForMinAndMax,
    roundingType: ROUND_UP,
  });
  const yMin = roundToNearestIncrement({
    value: Math.min(minVal, averagePrice || minVal),
    nearestIncrement: yAxisRoundingIncrementForMinAndMax,
    roundingType: ROUND_DOWN,
  });

  const yDelta = yMax - yMin;

  // three ticks on the y-axis: [min, half-way point, max]
  const yAxisTicks = [yMin, yMin + yDelta * 0.5, yMax];
  const dealRating = getDealRating(dealType);

  const dotProps = {
    stroke: '#319960',
    priceIncreaseDotColor: '#D65662',
    currentPriceRadius: '5',
    defaultRadius: '5',
    dealType: dealRating,
    setTooltipData,
  };

  const chart = (
    <div style={{ width: '100%', height: GRAPH_HEIGHT }} className="pos-r">
      <ResponsiveContainer width="100%" height={GRAPH_HEIGHT} debounce={DEBOUNCE} className="mt-1">
        <ComposedChart height={GRAPH_HEIGHT} margin={margin} data={data}>
          <defs>
            <linearGradient
              id="colorUv"
              x1={148.15}
              y1={0.999996}
              x2={108.306}
              y2={138.436}
              gradientUnits="userSpaceOnUse"
            >
              <stop offset={0.287339} stopColor="#B8E5CC" stopOpacity={0.35} />
              <stop offset={1} stopColor="white" />
            </linearGradient>
            <linearGradient id="colorUv2" x1={0.5} y1={26.5} x2={248.31} y2={65.6657} gradientUnits="userSpaceOnUse">
              <stop stopColor="white" stopOpacity={0.8} />
              <stop offset={0.185} stopColor="white" stopOpacity={0} />
              <stop offset={0.865} stopColor="white" stopOpacity={0} />
              <stop offset={1} stopColor="white" stopOpacity={0.8} />
            </linearGradient>
          </defs>
          <XAxis dataKey="name" tickLine={false} interval="preserveStartEnd" axisLine />
          <YAxis
            tickLine={false}
            domain={[yMin, yMax]}
            tickFormatter={formatPriceInThousands}
            ticks={yAxisTicks}
            axisLine
          />
          <Area type="monotone" dataKey="amount" stroke={AREA_STROKE_COLOR} fillOpacity={1} fill="url(#colorUv)" />
          <Area type="monotone" dataKey="amount" stroke={AREA_STROKE_COLOR} fillOpacity={1} fill="url(#colorUv2)" />
          <Line type="monotone" dataKey="amount" stroke="url(#gradient)" dot={<GraphActiveDot {...dotProps} />} />
          {!!averagePrice && <ReferenceLine y={averagePrice} stroke="#64748B" strokeDasharray="3 3" />}
        </ComposedChart>
      </ResponsiveContainer>
      {dealRating !== NO_RATING && <PriceDropGraphTooltip tooltipData={tooltipData} dealType={dealType} />}
    </div>
  );

  return (
    <>
      <Row className="price-drop-graph px-0_5">
        <Col className={classnames('small', wrapperClasses)}>{chart}</Col>
      </Row>
      {!!averagePrice && (
        <div className="medium text-cool-gray-40 text-end mb-1_5">
          <span className="text-cool-gray-50">---</span> average market price: {formatPriceStringV2(averagePrice)}
        </div>
      )}
    </>
  );
};
PriceDropGraph.propTypes = {
  currentPrice: PropTypes.number.isRequired,
  history: PropTypes.arrayOf(
    PropTypes.shape({
      amount: PropTypes.number,
      timestamp: PropTypes.number,
    })
  ),
  margin: PropTypes.shape({
    top: PropTypes.number,
    right: PropTypes.number,
    left: PropTypes.number,
    bottom: PropTypes.number,
  }),
  firstPublishedDate: PropTypes.number,
  wrapperClasses: PropTypes.string,
  showPriceChanges: PropTypes.bool,
  dealType: PropTypes.string,
};
